/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer, useLocalStore } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { SavingState } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getDTActiveStepName } from 'src/core/utils/telemetryUtils';
import { TargetFile, useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { ResourceValidationResult, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';
import { TranslationAdditionalOptionsPage } from 'src/translation/components/TranslationAdditionalOptionsPage';
import { TranslationBasicInfoPage } from 'src/translation/components/TranslationBasicInfoPage';
import { TranslationConfigurationPage } from 'src/translation/components/TranslationConfigurationPage';
import { TranslationFilesDestinationPage } from 'src/translation/components/TranslationFilesDestinationPage';
import { TranslationFilesSelectionPage } from 'src/translation/components/TranslationFilesSelectionPage';
import { TranslationReviewAndFinishPage } from 'src/translation/components/TranslationReviewAndFinishPage';
import { TranslationStepsNavigator } from 'src/translation/components/TranslationStepsNavigator';
import { getDtoFromStorage, removeDtoFromStorage, saveTranslationJobDtoToStorage } from 'src/translation/utils/translationCreationUtils';

// Component types
export enum TranslationCreationSteps {
    InitConfiguration = 0,
    BasicInformation = 1,
    FileSelection = 2,
    FileDestination = 3,
    AdditionalOptions = 4,
    ReviewAndFinish = 5
}

export enum TranslationFileSourceOptions {
    LocalStorage = 'localStorage',
    BlobStorage = 'blobStorage'
}

export type LanguageInfo = {
    key: string;
    value: string;
};

export type TranslationInfoDto = {
    storageAccount: string;
    sourceLanguage: LanguageInfo;
    targetLanguages: LanguageInfo[];
    filesSelected: TargetFile[];
    filesLocation: TranslationFileSourceOptions;
    isContainerSelectAll: boolean;
    isDownloadToLocalStorage: boolean;
    isUploadToBlobStore: boolean;
    destinationContainerName: string;
    uploadContainerName: string;
    sourceContainerName: string;
    customTranslatorModelId: string;
    glossaryFiles: TargetFile[];
    selectedLocalFiles: File[];
    selectedBlobFiles: AzureBlobStore[];
    destinationFolder: string;
};

// Component messages
const messages = defineMessages({
    close: { id: 'translation.translationCreationPage.close', defaultMessage: 'Close' },
    cancel: { id: 'translation.translationCreationPage.cancel', defaultMessage: 'Cancel' },
    goToSettings: { id: 'translation.translationCreationPage.goToSettings', defaultMessage: 'Go to Settings' },
    translationPageTitle: { id: 'translation.translationCreationPage.translationPageTitle', defaultMessage: 'Create a translation job' },
    autoDetectLanguageItem: { id: 'translation.translationCreationPage.autoDetectLanguageItem', defaultMessage: 'Auto-detect language' },
    translationPageSubtitle: {
        id: 'translation.translationCreationPage.translationPageSubtitle',
        defaultMessage: 'Start a translation job to batch translate multiple documents at once.'
    }
});

// Component styles
const { styleComponent } = getStylistV2('TranslationCreationPage');
const theme = getScopedTheme('TranslationCreationPage');
const borderStyle = `solid 1px ${theme.borderColor}`;

const NavigatorWrapper = styleComponent(Stack.Item)(
    'NavigatorWrapper',
    (_, cssVars) => ({
        width: cssVars.width,
        borderRight: borderStyle,
        paddingTop: '34px'
    }),
    {
        cssVars: { width: '' },
        cssVarsSetter: ({ horizontal }: { horizontal: boolean }) => ({
            width: horizontal ? '240px' : '100%'
        })
    }
);

const pageStepStyles = classNamesFunction<IStackProps, IStackItemStyles>()({ root: { paddingTop: '21px', position: 'relative' } });
const headerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { borderBottom: borderStyle, paddingLeft: '28px', paddingBottom: '13px' }
});

// Component related logic and JSX
export const TranslationCreationPage = observer(() => {
    const { localize } = useLocalization();
    const history = useHistory();
    const { isLargeOrGreater } = useResponsiveMode();

    const { rootStore, dispatcher } = useGlobalState();
    const blobFilesTracker = useProgressTracker();
    const { subscriptionId, csAccountStore, translatorStorageAccount } = rootStore.azureStore.session;

    const isReturningUser = !!translatorStorageAccount;

    const [translationConfigsDto, updateTranslationConfigsDto] = useUpdateState<TranslationInfoDto>(
        getDtoFromStorage(localize(messages.autoDetectLanguageItem), translatorStorageAccount)
    );

    const { supportedLanguages, getSupportedLanguages } = useTranslatorCapabilities();
    useAsyncEffect(async () => {
        if (csAccountStore.kind === 'TextTranslation') {
            getSupportedLanguages();
        }
    }, [csAccountStore?.kind]);

    const storageAccountTracker = useProgressTracker();
    const storageContainerTracker = useProgressTracker();
    const resourceTracker = useProgressTracker();

    const documentTranslationTracker = useProgressTracker({
        onComplete: () => {
            history.push(routeTable.translation.documentTranslationHistory());
        }
    });

    const filesStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobStore>>(createResourceStoreHouse);
    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
    const azureStorageAccountsStoreHouse = useLocalStore<ResourceStoreHouse<AzureStorageAccountStore>>(createResourceStoreHouse);
    const azureContainersStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobContainerStore>>(createResourceStoreHouse);

    const selectedStorageAccountStore = azureStorageAccountsStoreHouse.items.find(s => s.name === translationConfigsDto.storageAccount);

    const [_savingState, setSavingState] = React.useState<SavingState>();
    const [uploadedDocs, setUploadedDocs] = React.useState<string[]>([]);
    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: true });
    const [activeStep, setActiveStep] = React.useState(
        isReturningUser ? TranslationCreationSteps.BasicInformation : TranslationCreationSteps.InitConfiguration
    );

    React.useEffect(() => {
        if (csAccountStore?.id) {
            setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
        }
    }, [csAccountStore?.id]);

    React.useEffect(() => {
        if (!isValidResource.isValid) {
            removeDtoFromStorage();
        }
    }, [isValidResource.isValid]);

    React.useEffect(() => {
        if (subscriptionId && isValidResource.isValid && rootStore.azureStore.session.userTokens.armToken) {
            fetchStorageAccounts();
        }
        onDismiss();
    }, [subscriptionId, rootStore.azureStore.session.userTokens.armToken, isValidResource.isValid]);

    React.useEffect(() => {
        if (subscriptionId) {
            fetchTranslatorResources();
        }
    }, [subscriptionId]);

    const fetchStorageAccounts = () =>
        dispatcher.dispatch('getAzureStorageAccountsBySubscriptionId', {
            toastData: {},
            id: subscriptionId,
            tracker: storageAccountTracker,
            localStore: azureStorageAccountsStoreHouse
        });

    const fetchTranslatorResources = () => {
        dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
            toastData: {},
            id: subscriptionId,
            tracker: resourceTracker,
            localStoreHouse: resourcesStoreHouse,
            filterPredicate: AzureResourcesStrategies.translatorResourceStrategy
        });
    };

    const fetchStorageContainers = () => {
        if (azureContainersStoreHouse.tracker.isNotStarted) {
            dispatcher.dispatch('getAzureContainersByStorageAccountName', {
                toastData: {},
                subscriptionId,
                tracker: storageContainerTracker,
                name: translationConfigsDto.storageAccount,
                localStore: azureContainersStoreHouse,
                resourceGroupName: selectedStorageAccountStore?.resourceGroupName
            });
        }
    };

    const onChangeStorageContainer = (sourceContainerName: string) => {
        if (sourceContainerName) {
            dispatcher.dispatch('getAzureBlobsByContainerName', {
                toastData: {},
                tracker: blobFilesTracker,
                localStore: filesStoreHouse,
                storageAccountName: translationConfigsDto.storageAccount,
                containerName: sourceContainerName
            });
            updateTranslationConfigsDto({ sourceContainerName, selectedBlobFiles: [] });
        }
    };

    const updateStorageAccountInSession = (storageAccount: string) =>
        dispatcher.dispatch('updateTranslatorStorageAccountInSession', {
            storageAccountName: storageAccount
        });

    const onSubmit = () => {
        setSavingState('saved');

        logDTButtonClick('document translation', 'jobCreation', 'footerButton', 'startTranslationJob', getDTActiveStepName(activeStep));

        if (activeStep === TranslationCreationSteps.ReviewAndFinish) {
            saveTranslationJobDtoToStorage(translationConfigsDto);

            dispatcher.dispatch('translateDocuments', {
                files: translationConfigsDto.filesSelected,
                glossaryFiles: translationConfigsDto.glossaryFiles,
                isAllFiles: translationConfigsDto.isContainerSelectAll,
                storageAccountName: translationConfigsDto.storageAccount,
                sourceLanguage: translationConfigsDto.sourceLanguage.key,
                destinationFolder: translationConfigsDto.destinationFolder,
                resourceGroupName: selectedStorageAccountStore?.resourceGroupName,
                targetLanguages: translationConfigsDto.targetLanguages.map(l => l.key),
                customTranslatorModelId: translationConfigsDto.customTranslatorModelId,
                downloadTranslatedFiles: translationConfigsDto.isDownloadToLocalStorage,
                destinationContainerName: translationConfigsDto.destinationContainerName,
                tracker: documentTranslationTracker,
                toastData: {}
            });
        }
    };

    const onDismiss = () => {
        setSavingState('saved');

        if (isReturningUser) {
            setActiveStep(TranslationCreationSteps.BasicInformation);
            logDTButtonClick('document translation', 'jobCreation', 'footerButton', 'discard', getDTActiveStepName(activeStep));
        } else {
            setActiveStep(TranslationCreationSteps.InitConfiguration);
        }
        updateTranslationConfigsDto(getDtoFromStorage(localize(messages.autoDetectLanguageItem), translatorStorageAccount));
    };

    const onPrevious = () => {
        logDTButtonClick('document translation', 'jobCreation', 'footerButton', 'back', getDTActiveStepName(activeStep));
        if (activeStep !== TranslationCreationSteps.InitConfiguration) {
            setActiveStep(activeStep - 1);
        }
    };

    const onNext = (dto: Partial<TranslationInfoDto>) => {
        setSavingState('dirty');

        updateTranslationConfigsDto(dto);

        logDTButtonClick('document translation', 'jobCreation', 'footerButton', 'next', getDTActiveStepName(activeStep));

        if (activeStep !== TranslationCreationSteps.ReviewAndFinish) {
            setActiveStep(activeStep + 1);
        }

        if (activeStep === TranslationCreationSteps.InitConfiguration) {
            updateStorageAccountInSession(dto.storageAccount);
        }
    };

    const onResourceChange = (resourceId: string) =>
        dispatcher.dispatch('switchAzureCsAccountInSession', {
            toastData: {},
            tracker: resourceTracker,
            csAccountStore: resourcesStoreHouse.items.find(r => r.id === resourceId)
        });

    const onChangeSelectedStorageAccount = (storageAccount: string) => {
        azureContainersStoreHouse.tracker.setState('notStarted');
        updateTranslationConfigsDto({ storageAccount });
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case TranslationCreationSteps.BasicInformation: {
                return (
                    <TranslationBasicInfoPage
                        onSubmit={onSubmit}
                        onDismiss={onDismiss}
                        onNext={(sourceLanguage, targetLanguages) => onNext({ sourceLanguage, targetLanguages })}
                        onPrevious={onPrevious}
                        translationConfigsDto={translationConfigsDto}
                        activeStep={activeStep}
                        isReturningUser={isReturningUser}
                        supportedLanguages={supportedLanguages}
                        onChangeSourceLanguage={sourceLanguage => updateTranslationConfigsDto({ sourceLanguage })}
                        onChangeTargetLanguages={targetLanguages => updateTranslationConfigsDto({ targetLanguages })}
                        onChangeSavingState={setSavingState}
                        selectedStorageAccount={translationConfigsDto.storageAccount}
                    />
                );
            }
            case TranslationCreationSteps.FileSelection: {
                return (
                    <TranslationFilesSelectionPage
                        onSubmit={onSubmit}
                        onDismiss={onDismiss}
                        onNext={(
                            filesLocation,
                            uploadContainerName,
                            filesSelected,
                            isContainerSelectAll,
                            selectedLocalFiles,
                            selectedBlobFiles
                        ) =>
                            onNext({
                                filesLocation,
                                filesSelected,
                                uploadContainerName,
                                isContainerSelectAll,
                                selectedLocalFiles,
                                selectedBlobFiles
                            })
                        }
                        onPrevious={onPrevious}
                        activeStep={activeStep}
                        uploadedDocs={uploadedDocs}
                        blobFiles={filesStoreHouse.items}
                        isReturningUser={isReturningUser}
                        translationConfigsDto={translationConfigsDto}
                        onChangeStorageContainer={onChangeStorageContainer}
                        storageContainers={azureContainersStoreHouse?.items}
                        storageAccounts={azureStorageAccountsStoreHouse.items}
                        fetchStorageAccountContainers={fetchStorageContainers}
                        isBlobFilesLoading={filesStoreHouse.tracker.isRunning}
                        selectedStorageAccountStore={selectedStorageAccountStore}
                        createdStorageContainerName={translationConfigsDto.uploadContainerName}
                        onUploadedDocsChange={newUploadedDocs => setUploadedDocs([...uploadedDocs, ...newUploadedDocs])}
                        onChangeSelectedBlobFiles={selectedBlobFiles => updateTranslationConfigsDto({ selectedBlobFiles })}
                        onChangeSelectedLocalFiles={selectedLocalFiles => updateTranslationConfigsDto({ selectedLocalFiles })}
                        isStorageContainersLoading={azureContainersStoreHouse.tracker.isRunning || storageContainerTracker.isRunning}
                        isStorageAccountsLoading={azureStorageAccountsStoreHouse.tracker.isRunning || storageAccountTracker.isRunning}
                    />
                );
            }
            case TranslationCreationSteps.FileDestination: {
                return (
                    <TranslationFilesDestinationPage
                        onSubmit={onSubmit}
                        onDismiss={onDismiss}
                        onPrevious={onPrevious}
                        onNext={(isDownloadToLocalStorage, isUploadToBlobStore, selectedContainer, destinationFolder) => {
                            onNext({
                                isDownloadToLocalStorage,
                                isUploadToBlobStore,
                                destinationContainerName:
                                    translationConfigsDto.filesLocation === TranslationFileSourceOptions.LocalStorage &&
                                    !isUploadToBlobStore
                                        ? LuisConstants.TranslationTool.AutoGeneratedContainerName
                                        : selectedContainer,
                                destinationFolder
                            });
                        }}
                        activeStep={activeStep}
                        isReturningUser={isReturningUser}
                        translationConfigsDto={translationConfigsDto}
                        fetchStorageContainers={fetchStorageContainers}
                        storageContainers={azureContainersStoreHouse?.items}
                        selectedLocalFiles={translationConfigsDto.filesLocation}
                        selectedStorageAccount={translationConfigsDto.storageAccount}
                        isStorageContainersLoading={azureContainersStoreHouse.tracker.isRunning || storageContainerTracker.isRunning}
                    />
                );
            }
            case TranslationCreationSteps.AdditionalOptions: {
                return (
                    <TranslationAdditionalOptionsPage
                        onSubmit={onSubmit}
                        onDismiss={onDismiss}
                        onPrevious={onPrevious}
                        activeStep={activeStep}
                        isReturningUser={isReturningUser}
                        supportedLanguages={supportedLanguages}
                        translationConfigsDto={translationConfigsDto}
                        storageAccounts={azureStorageAccountsStoreHouse.items}
                        selectedStorageAccount={translationConfigsDto.storageAccount}
                        selectedTargetLanguages={translationConfigsDto.targetLanguages}
                        resourceGroupName={selectedStorageAccountStore?.resourceGroupName}
                        createdStorageContainerName={LuisConstants.TranslationTool.AutoGeneratedContainerName}
                        onNext={(customTranslatorModelId, glossaryFiles) => onNext({ customTranslatorModelId, glossaryFiles })}
                    />
                );
            }
            case TranslationCreationSteps.ReviewAndFinish: {
                return (
                    <TranslationReviewAndFinishPage
                        onSubmit={onSubmit}
                        onDismiss={onDismiss}
                        onPrevious={onPrevious}
                        activeStep={activeStep}
                        isReturningUser={isReturningUser}
                        translationConfigsDto={translationConfigsDto}
                        isLoading={documentTranslationTracker.isRunning}
                    />
                );
            }
            case TranslationCreationSteps.InitConfiguration:
            default: {
                return (
                    <TranslationConfigurationPage
                        onSubmit={onSubmit}
                        onDismiss={onDismiss}
                        onPrevious={onPrevious}
                        activeStep={activeStep}
                        isReturningUser={isReturningUser}
                        onResourceChange={onResourceChange}
                        selectedResourceStore={csAccountStore}
                        onRefreshStorageAccounts={fetchStorageAccounts}
                        translatorResources={resourcesStoreHouse.items}
                        storageAccountsError={storageAccountTracker.error}
                        onNext={storageAccount => onNext({ storageAccount })}
                        storageAccounts={azureStorageAccountsStoreHouse.items}
                        onStorageAccountChange={onChangeSelectedStorageAccount}
                        selectedStorageAccount={translationConfigsDto.storageAccount}
                        isResourcesLoading={resourcesStoreHouse.tracker.isRunning || resourceTracker.isRunning}
                        isStorageAccountsLoading={
                            azureStorageAccountsStoreHouse.tracker.isRunning || storageAccountTracker.isRunning || !rootStore.azureStore.session.userTokens.armToken
                        }
                    />
                );
            }
        }
    };

    return (
        <Page pageName="translationJobCreationScreen" title={localize(messages.translationPageTitle)} reactToZoomLevel={false}>
            <Stack verticalFill>
                <Stack tokens={{ childrenGap: 5 }} styles={headerStyles}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <EditableTitle title={localize(messages.translationPageTitle)} />
                    </Stack>
                    <LuisText>{localize(messages.translationPageSubtitle)}</LuisText>
                </Stack>

                <Stack horizontal={isLargeOrGreater} verticalFill styles={{ root: { paddingLeft: '28px' } }}>
                    <NavigatorWrapper customProps={{ horizontal: isLargeOrGreater }}>
                        <TranslationStepsNavigator
                            activeStep={activeStep}
                            isReturningUser={isReturningUser}
                            onActiveStepChange={setActiveStep}
                            translationConfigsDto={translationConfigsDto}
                        />
                    </NavigatorWrapper>

                    <Stack grow tokens={{ childrenGap: 21 }} styles={isValidResource && pageStepStyles}>
                        {renderStepContent()}
                    </Stack>
                </Stack>
            </Stack>
        </Page>
    );
});
